import React from 'react';
import {
  Button,
  FooterActionBar,
  Icon,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../../../shared/hooks';
import type { LicenseFormData } from '../Overview/AddLicensePanel/type';

import './SaveLicenseConfirmModal.css';

export interface SaveLicenseConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRunSimulation: () => void;
  licenseData: LicenseFormData | null;
}

/**
 *
 */
function SaveLicenseConfirmModal({
  isOpen,
  onClose,
  onRunSimulation,
  licenseData,
}: SaveLicenseConfirmModalProps): React.ReactElement | null {
  const localize = useInvertedLocalize();

  if (!isOpen || !licenseData) {
    return null;
  }

  const estimatedAnnualValue = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(licenseData.totalFUE * licenseData.costPerFue);

  const contentBody = (
    <div className='LicenseManagement-SaveLicenseConfirmModal-content'>
      <div className='LicenseManagement-SaveLicenseConfirmModal-warningRow'>
        <Icon
          kind='AlertInfoFilled'
          size='medium'
          className='LicenseManagement-SaveLicenseConfirmModal-warningIcon'
        />
        <Typography
          kind='body2'
          className='LicenseManagement-SaveLicenseConfirmModal-warningText'>
          {localize(
            'Running a simulation will validate your FUE allocation against current usage.'
          )}
        </Typography>
      </div>
      <div className='LicenseManagement-SaveLicenseConfirmModal-details'>
        <div className='LicenseManagement-SaveLicenseConfirmModal-detailRow'>
          <Typography
            kind='body2'
            className='LicenseManagement-SaveLicenseConfirmModal-detailKey'>
            {localize('License name')}
          </Typography>
          <Typography
            kind='body2-bold'
            className='LicenseManagement-SaveLicenseConfirmModal-detailValue'>
            {licenseData.licenseName || '—'}
          </Typography>
        </div>
        <div className='LicenseManagement-SaveLicenseConfirmModal-detailRow'>
          <Typography
            kind='body2'
            className='LicenseManagement-SaveLicenseConfirmModal-detailKey'>
            {localize('Total FUE')}
          </Typography>
          <Typography
            kind='body2-bold'
            className='LicenseManagement-SaveLicenseConfirmModal-detailValue'>
            {licenseData.totalFUE.toLocaleString('en-US')}
          </Typography>
        </div>
        <div className='LicenseManagement-SaveLicenseConfirmModal-detailRow'>
          <Typography
            kind='body2'
            className='LicenseManagement-SaveLicenseConfirmModal-detailKey'>
            {localize('Estimated annual value')}
          </Typography>
          <Typography
            kind='body2-bold'
            className='LicenseManagement-SaveLicenseConfirmModal-detailValue'>
            {estimatedAnnualValue}
          </Typography>
        </div>
        {licenseData.startDate && licenseData.endDate && (
          <div className='LicenseManagement-SaveLicenseConfirmModal-detailRow'>
            <Typography
              kind='body2'
              className='LicenseManagement-SaveLicenseConfirmModal-detailKey'>
              {localize('Contract period')}
            </Typography>
            <Typography
              kind='body2-bold'
              className='LicenseManagement-SaveLicenseConfirmModal-detailValue'>
              {localize(
                `${licenseData.startDate} \u2013 ${licenseData.endDate}`
              )}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );

  const footer = (
    <FooterActionBar
      size='large'
      className='LicenseManagement-SaveLicenseConfirmModal-footer'>
      <div className='LicenseManagement-SaveLicenseConfirmModal-footerActions'>
        <Button kind='ghost' onClick={onClose}>
          {localize('Cancel')}
        </Button>
        <Button kind='filled' onClick={onRunSimulation}>
          {localize('Run Simulation')}
        </Button>
      </div>
    </FooterActionBar>
  );

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={onClose}
      title={localize('License saved! Ready to run simulation?')}
      subtitle={localize(
        'Review the details below before running the simulation.'
      )}
      contentBody={contentBody}
      FooterActionBarComp={footer}
      dataTheme='dark'
      overlayBackgroundColor='rgba(128, 128, 128, 0.5)'
      className='LicenseManagement-SaveLicenseConfirmModal'
    />
  );
}

export default SaveLicenseConfirmModal;
